import { toast } from "sonner";
import { logger } from "../../logger";
import {
  bulkDeleteHandler,
  bulkExportHandler,
  type BulkActionConfig,
  type BulkActionMutations,
} from "./genericBulkActions.core";
import {
  createContactBulkActions,
  createTaskBulkActions,
  createTicketBulkActions,
  createTimeEntryBulkActions,
  createSocialMediaPostBulkActions,
} from "./genericBulkActions.entityConfigs";

// Re-export core types and handlers so existing import path remains stable.
export {
  bulkDeleteHandler,
  bulkExportHandler,
  bulkExportEmailsHandler,
  bulkStatusUpdateHandler,
} from "./genericBulkActions.core";
export type {
  BulkMutationLike,
  BulkActionConfig,
  BulkActionMutations,
  BulkUpdateResult,
} from "./genericBulkActions.core";

// Re-export entity-specific factories.
export {
  createTaskBulkActions,
  createContactBulkActions,
  createTimeEntryBulkActions,
  createTicketBulkActions,
  createSocialMediaPostBulkActions,
} from "./genericBulkActions.entityConfigs";

// ============================================================================
// GENERIC BULK ACTION CREATOR
// ============================================================================

/**
 * Create a generic bulk action handler for any entity
 */
export function createGenericBulkActionHandler<T extends { id: string }>(
  entityName: string,
  bulkActions: BulkActionConfig[],
  mutations: BulkActionMutations
) {
  return async (actionId: string, selectedItems: T[]) => {
    try {
      const action = bulkActions.find((a) => a.id === actionId);
      if (!action) {
        throw new Error(`Unknown bulk action: ${actionId}`);
      }

      const items = selectedItems as Array<{ id: string } & Record<string, unknown>>;

      if (action.handler) {
        await action.handler(items, mutations, entityName);
      } else {
        // Fallback to generic handlers
        switch (actionId) {
          case "bulk_delete":
            await bulkDeleteHandler(items, mutations, entityName);
            break;
          case "bulk_export":
            await bulkExportHandler(items, mutations, entityName);
            break;
          case "bulk_export_emails":
            // This action is now handled by UniversalEntityPageContext.bulkActions
            // It should not reach here if properly configured
            toast.error("Email export action not properly configured");
            break;
          default:
            toast.error(`Bulk action ${actionId} not implemented`);
        }
      }
    } catch (error) {
      logger.error("Bulk action error", error);
      toast.error(
        `Failed to perform bulk action: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Get bulk actions for a specific entity
 */
export function getBulkActionsForEntity(
  entityName: string,
  mutations: BulkActionMutations
): BulkActionConfig[] {
  switch (entityName) {
    case "tasks":
      return createTaskBulkActions(mutations);
    case "contacts":
      return createContactBulkActions(mutations);
    case "time_entries":
      return createTimeEntryBulkActions(mutations);
    case "tickets":
      return createTicketBulkActions(mutations);
    case "social_media_posts":
      return createSocialMediaPostBulkActions(mutations);
    default:
      // Return basic bulk actions for unknown entities
      return [
        {
          id: "bulk_delete",
          label: "Delete",
          icon: "trash",
          variant: "destructive",
          handler: bulkDeleteHandler,
        },
        {
          id: "bulk_export",
          label: "Export",
          icon: "download",
          handler: bulkExportHandler,
        },
      ];
  }
}
